import { FC, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Checkbox, FormControlLabel, Stack } from '@mui/material'

import { LanguageMaps } from '../../../../api/language'
import { InputTranslator } from '../../../../components/InputTranslator/InputTranslator'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { categoryLanguages } from '../../helpers/categoryLanguageHelpers'
import { CategorySaveBody, InternalCategoryRecord } from '../../types/Categories'
import { CategoryIconUpload } from '../CategoryIconUpload/CategoryIconUpload'
import { CategoryFormValues, needsIcon, toCategoryFormValues, toCategorySaveBody } from '../helpers/categoryHelpers'

type CategoryEditDialogProps = {
  open: boolean
  category?: InternalCategoryRecord
  languageMaps?: LanguageMaps
  isSaving?: boolean
  onClose: () => void
  onSave: (body: CategorySaveBody) => void
}

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const CategoryEditDialog: FC<CategoryEditDialogProps> = ({ open, category, languageMaps, isSaving, onClose, onSave }) => {
  const { t } = useTranslation()
  const [isUploading, setIsUploading] = useState(false)
  const { control, handleSubmit, reset, formState, watch } = useForm<CategoryFormValues>({
    mode: 'onChange',
    defaultValues: toCategoryFormValues(category, languageMaps),
  })

  useEffect(() => {
    if (open) reset(toCategoryFormValues(category, languageMaps))
    // The form is only reset when the dialog opens, so a refetch while editing doesn't drop the edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, category, reset])

  const active = watch('active')
  const icon = watch('icon')
  const iconMissing = needsIcon({ active, icon })
  const submit = handleSubmit((values) => onSave(toCategorySaveBody(category, values)))

  return (
    <InternalFormDialog
      open={open}
      title={category ? t('internal-analysts:categories_edit') : t('internal-analysts:categories_add')}
      onClose={onClose}
      onSave={submit}
      saveDisabled={!formState.isValid || iconMissing || isUploading || isSaving}
    >
      <Stack spacing={2} pt={1}>
        <Controller
          name="names"
          control={control}
          rules={{ validate: (names) => !!names.en?.trim() }}
          render={({ field }) => (
            <InputTranslator
              value={field.value}
              onChange={field.onChange}
              label={t('internal-analysts:categories_name')}
              dialogTitle={t('internal-analysts:categories_name')}
              selectedLanguages={categoryLanguages}
            />
          )}
        />
        <Controller
          name="active"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
              label={t('internal-analysts:categories_active')}
            />
          )}
        />
        <Controller
          name="icon"
          control={control}
          render={({ field }) => (
            <CategoryIconUpload
              value={field.value}
              onChange={field.onChange}
              onUploadingChange={setIsUploading}
              error={iconMissing ? t('internal-analysts:categories_icon_required') : undefined}
            />
          )}
        />
      </Stack>
    </InternalFormDialog>
  )
}
